import clsx from "clsx";
import { navigate } from "gatsby";
import React, { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { useHeaderMenu } from "../../../../hooks/use-header-menu";
import { useGetPagePathByLocale } from "../../../../hooks/use-page-full-path";
import {
  getAllCountryValues,
  getCountryLanguageFromLocaleCode,
  getNativeCountryName,
  getNativeLanguageName,
  getNodeLocaleFromCountryLanguage,
  getSupportedLanguagesForCountry,
} from "../../../../utils/i18n";
import { getLocalizedPath } from "../../../../utils/pages";
import { Button } from "../../../buttons/button";
import { Select } from "../../../forms";

export interface LocaleSelectionFormTypes {
  country: string;
  language: string;
}

export interface LocaleSelectionFormProps {
  className?: string;
}

export function LocaleSelectionForm({ className }: LocaleSelectionFormProps) {
  const {
    labels,
    pageContentfulId,
    internalPagePath,
    headerMenu: { node_locale },
  } = useHeaderMenu();

  const getPagePathByLocale = useGetPagePathByLocale();

  const initialValues = getCountryLanguageFromLocaleCode(node_locale);
  const allCountryValues = getAllCountryValues();

  const { handleSubmit, register, watch, setValue } = useForm<LocaleSelectionFormTypes>({
    defaultValues: {
      country: initialValues.country ?? "",
      language: initialValues.language ?? "",
    },
  });

  const onSubmit = useCallback(
    ({ country, language }: LocaleSelectionFormTypes) => {
      const locale = getNodeLocaleFromCountryLanguage(country, language);
      if (pageContentfulId) {
        const localizedPagePath = getPagePathByLocale(locale);
        if (localizedPagePath && typeof window !== "undefined") {
          window.location.href = [window.location.origin, localizedPagePath, window.location.search]
            .filter(Boolean)
            .join("");
        } else {
          navigate(getLocalizedPath("/", locale));
        }
      } else if (internalPagePath) {
        const redirectPath = getLocalizedPath(internalPagePath, locale);
        navigate(redirectPath);
      }
    },
    [pageContentfulId, internalPagePath, getPagePathByLocale]
  );

  const [country, language] = watch(["country", "language"]);

  const availableLanguagesForCountry = useMemo(() => {
    return getSupportedLanguagesForCountry(country);
  }, [country]);

  useEffect(() => {
    const language = watch("language");
    if (!availableLanguagesForCountry.includes(language)) {
      setValue("language", availableLanguagesForCountry[0] ?? "");
    }
  }, [language, watch, availableLanguagesForCountry, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex-1">
      <div className={clsx("flex flex-col gap-4 xs:max-sm:items-end", className)}>
        <Select
          label={labels["header-menu-locale-selector-country"]}
          className="w-full lg:w-2/3 xl:w-1/2"
          {...register("country")}
        >
          {allCountryValues.map((countryCode, i) => {
            const countryLabel = getNativeCountryName(countryCode);
            return (
              <option value={countryCode} key={i}>
                {countryLabel}
              </option>
            );
          })}
        </Select>
        <Select
          disabled={!country || !availableLanguagesForCountry.length}
          label={labels["header-menu-locale-selector-language"]}
          className="w-full lg:w-2/3 xl:w-1/2"
          {...register("language")}
        >
          {availableLanguagesForCountry.map((langCode, i) => (
            <option key={i} value={langCode}>
              {getNativeLanguageName(langCode) ?? langCode}
            </option>
          ))}
        </Select>
      </div>
      <Button
        disabled={!country || !language}
        type="submit"
        variant="primary"
        size="sm"
        className="w-fit mt-4 lg:mt-5"
      >
        {labels["header-menu-locale-selector-submit"]}
      </Button>
    </form>
  );
}
